
import React, { useState, useMemo, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate, Navigate, useParams } from 'react-router-dom';
import { Dashboard } from './pages/member/Dashboard';
import { Search } from './pages/game/Search';
import { CollectionsPage } from './pages/game/Collections';
import { CollectionCardsPage } from './pages/member/CollectionCardsPage';
import { DeckBuilderPage } from './pages/game/DeckBuilder';
import { Stores } from './pages/member/Stores';
import { Login } from './pages/Login';
import { Profile } from './pages/member/Profile';
import { Products } from './pages/member/Products';
import { Tickets } from './pages/member/Tickets';
import { Orders } from './pages/member/Orders';
import { OrderDetails } from './pages/member/OrderDetails';
import { StoreProfile } from './pages/member/StoreProfile';
import { EventDetails } from './pages/member/EventDetails';
import { CartPage } from './pages/member/Cart';
import { FoldersPage } from './pages/member/Folders';
import { FolderDetails } from './pages/member/FolderDetails';
import { DeckView } from './pages/game/DeckView';
import { DecksPage } from './pages/game/Decks';
import { MyDecksPage } from './pages/game/MyDecks';
import { GlossaryPage } from './pages/game/Glossary';
import { HowToPlayPage } from './pages/game/HowToPlay';
import { SupportPage } from './pages/member/Support';
import { Notifications } from './pages/member/Notifications';
import { Trades } from './pages/member/Trades';
import { Tournaments } from './pages/member/Tournaments';
import { Social } from './pages/member/Social';
import { Reports } from './pages/member/Reports';
import { Meta } from './pages/member/Meta';
import { Tools } from './pages/game/Tools';
import { EditProfile } from './pages/member/EditProfile';
import { UserConnections } from './pages/member/UserConnections';
import { ManageStock } from './pages/shopkeeper/ManageStock';
import { ManageInventory } from './pages/shopkeeper/ManageInventory';
import { ManageCustomerCredits } from './pages/shopkeeper/ManageCustomerCredits';
import { ManageStore } from './pages/shopkeeper/ManageStore';
import { StoreHours } from './pages/shopkeeper/StoreHours';
import { StoreSchedule } from './pages/shopkeeper/StoreSchedule';
import { StoreGames } from './pages/shopkeeper/StoreGames';
import { StoreShippingPayment } from './pages/shopkeeper/StoreShippingPayment';
import { MercadoPagoSetup } from './pages/shopkeeper/MercadoPagoSetup';
import { ManageTournaments } from './pages/shopkeeper/ManageTournaments';
import { ManageTournamentDetails } from './pages/shopkeeper/ManageTournamentDetails';
import { CreateTournament } from './pages/shopkeeper/CreateTournament';
import { ImportTournamentResults } from './pages/shopkeeper/ImportTournamentResults';
import { PedidoBalcao } from './pages/shopkeeper/PedidoBalcao';
import { ReceivedOrders } from './pages/shopkeeper/ReceivedOrders';
import { LojistaOrderDetails } from './pages/shopkeeper/LojistaOrderDetails';
import { ManageAddresses } from './pages/member/ManageAddresses';
import { ResetPassword } from './pages/ResetPassword';
import { SettingsPage } from './pages/member/SettingsPage';
import { TermsPage } from './pages/member/Terms';
import { LojistaTermsPage } from './pages/shopkeeper/LojistaTerms';
import { Product, CartItem, GameType } from './types';
import { AuthProvider, useAuth } from './components/AuthProvider';
import { NotificationProvider } from './components/member/NotificationProvider';
import { LoginPromptProvider } from './contexts/LoginPromptContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { GuestGate } from './components/GuestGate';
import { ErrorBoundary } from './components/ErrorBoundary';

import { getCardgames, getNotifications, getGameIcon } from './services/supabaseService';

import { Layout } from './components/Layout';
import { Landing } from './pages/Landing';
import { MaintenancePage } from './pages/Maintenance';
import { SEO } from './components/SEO';

const ProfileRedirect: React.FC = () => {
  const { userId } = useParams();
  return <Navigate to={userId ? `/perfil/${userId}` : '/perfil'} replace />;
};

const UserConnectionsRedirect: React.FC<{ tab: 'seguidores' | 'seguindo' }> = ({ tab }) => {
  const { userId } = useParams();
  return <Navigate to={userId ? `/perfil/${userId}/conexoes?tab=${tab}` : `/perfil/conexoes?tab=${tab}`} replace />;
};

const AppContent: React.FC = () => {
  const location = useLocation();
  const { user, supabaseUser, login, logout, loading, confirmAndLoadProfile } = useAuth();
  const navigate = useNavigate();

  const [cart, setCart] = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem('cardumy_cart');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      console.error('Error loading cart from localStorage:', e);
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem('cardumy_cart', JSON.stringify(cart));
  }, [cart]);

  const [activeGame, setActiveGame] = useState<number | 'All'>(() => {
    const saved = localStorage.getItem('cardumy_active_game');
    if (saved === 'All' || !saved) return 'All';
    return Number(saved);
  });

  useEffect(() => {
    localStorage.setItem('cardumy_active_game', activeGame.toString());
  }, [activeGame]);

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [dbGames, setDbGames] = useState<{id: any, name: string, slug?: string, homura?: boolean, kyubey?: boolean, sauce?: string | null, card_back_url?: string, isAvailable?: boolean, apiBaseUrl?: string}[]>([]);
  const [unreadNotifs, setUnreadNotifs] = useState(0);

  useEffect(() => {
    const fetchGames = async () => {
      const g = await getCardgames();
      setDbGames(g);
    };
    fetchGames();
  }, []);

  useEffect(() => {
    const fetchUnread = async () => {
      if (user) {
        const notifs = await getNotifications();
        if (Array.isArray(notifs)) {
          setUnreadNotifs(notifs.filter((n: any) => !n.is_read).length);
        } else {
          setUnreadNotifs(0);
        }
      }
    };
    fetchUnread();
    
    // Refresh every minute
    const interval = setInterval(fetchUnread, 60000);
    return () => clearInterval(interval);
  }, [user, location.pathname]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleImageError = (event: Event) => {
      const target = event.target as HTMLImageElement;
      if (target && target.tagName === 'IMG' && !(target as any)._isFallbackApplied) {
        (target as any)._isFallbackApplied = true;
        const FALLBACK_IMAGES = [
          'https://images.unsplash.com/photo-1614850523296-d8c1af93d400?auto=format&fit=crop&q=80&w=800',
          'https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&q=80&w=800',
          'https://images.unsplash.com/photo-1542751371-adc38448a05e?auto=format&fit=crop&q=80&w=800',
          'https://images.unsplash.com/photo-1511512578047-dfb367046420?auto=format&fit=crop&q=80&w=800',
          'https://images.unsplash.com/photo-1606103920295-9a091573f160?auto=format&fit=crop&q=80&w=800'
        ];
        const randomIndex = Math.floor(Math.random() * FALLBACK_IMAGES.length);
        target.src = FALLBACK_IMAGES[randomIndex];
        target.referrerPolicy = "no-referrer";
      }
    };
    window.addEventListener('error', handleImageError, true);
    return () => window.removeEventListener('error', handleImageError, true);
  }, []);

  const cartCount = useMemo(() => cart.reduce((acc, item) => acc + item.quantity, 0), [cart]);
  
  if (loading) {
    return (
      <div className="fixed inset-0 bg-slate-950 flex flex-col items-center justify-center z-[9999]">
        <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center animate-pulse shadow-2xl shadow-purple-600/20 mb-6">
          <i className="fas fa-fish-fins text-white text-3xl"></i>
        </div>
        <div className="flex flex-col items-center space-y-4">
          <h2 className="text-white font-black text-xl tracking-tight">Cardumy</h2>
          <div className="flex space-x-1">
            <div className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
            <div className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
            <div className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-bounce"></div>
          </div>
        </div>
      </div>
    );
  }

  // Check if system is in maintenance mode
  const isMaintenance = import.meta.env.VITE_MAINTENANCE_MODE === '1' || localStorage.getItem('cardumy_maintenance') === 'true';

  // Bypass route for maintenance page
  if (location.pathname === '/manutencao' || location.pathname === '/maintenance' || (isMaintenance && location.pathname !== '/login')) {
    return (
      <Routes>
        <Route path="/manutencao" element={<MaintenancePage />} />
        <Route path="/maintenance" element={<MaintenancePage />} />
        {isMaintenance && <Route path="*" element={<MaintenancePage />} />}
      </Routes>
    );
  }

  // Bypass route for password reset flow
  if (location.pathname === '/reset-password') {
    return (
      <Routes>
        <Route path="/reset-password" element={<ResetPassword />} />
      </Routes>
    );
  }
  
  // Confirmed user but profile still loading (should be fast)
  if (supabaseUser && (!user || dbGames.length === 0)) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center space-y-4">
        <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center animate-pulse shadow-2xl shadow-purple-600/20 mb-6">
          <i className="fas fa-fish-fins text-white text-3xl"></i>
        </div>
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
        <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest">Sincronizando Perfil...</p>
      </div>
    );
  }

  const addToCart = (product: Product & { maxPerUser?: number }) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      const limit = (product as any).maxPerUser || (existing as any)?.maxPerUser;
      if (existing) {
        if (limit && existing.quantity >= limit) {
          return prev;
        }
        return prev.map(item => item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item);
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const updateQuantity = (id: string, delta: number) => {
    setCart(prev => prev.map(item => {
      if (item.id === id) {
        const limit = (item as any).maxPerUser;
        const newQty = Math.max(0, item.quantity + delta);
        if (limit && delta > 0 && newQty > limit) {
          return item;
        }
        return { ...item, quantity: newQty };
      }
      return item;
    }).filter(item => item.quantity > 0));
  };

  const removeFromCart = (id: string) => {
    setCart(prev => prev.filter(item => item.id !== id));
  };

  const clearCart = (storeId?: string | number) => {
    if (storeId) {
      setCart(prev => prev.filter(item => (item.storeId || item.store_id) !== storeId));
    } else {
      setCart([]);
    }
  };

  return (
    <>
      <SEO />
      <Routes>
        {/* Route for Landing page when no session is active */}
      {!supabaseUser && <Route path="/" element={<Landing />} />}
      <Route path="/landing" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/manutencao" element={<MaintenancePage />} />
      <Route path="/maintenance" element={<MaintenancePage />} />

      {/* App Layout for dashboard, catalog exploration and feature routes */}
      <Route element={
        <Layout 
          cartCount={cartCount}
          unreadNotifs={unreadNotifs}
          isSidebarCollapsed={isSidebarCollapsed}
          setIsSidebarCollapsed={setIsSidebarCollapsed}
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
          activeGame={activeGame}
          setActiveGame={setActiveGame}
          dbGames={dbGames}
        />
      }>
        <Route path="/dashboard" element={<Dashboard activeGame={activeGame} setActiveGame={setActiveGame} />} />
        <Route path="/inicio" element={<Dashboard activeGame={activeGame} setActiveGame={setActiveGame} />} />
        {supabaseUser && (
          <Route path="/" element={<Dashboard activeGame={activeGame} setActiveGame={setActiveGame} />} />
        )}
        <Route path="/busca" element={<Search activeGame={activeGame} setActiveGame={setActiveGame} dbGames={dbGames} />} />
        <Route path="/colecoes" element={<CollectionsPage activeGame={activeGame} setActiveGame={setActiveGame} dbGames={dbGames} />} />
        <Route path="/colecoes/:gameId" element={<CollectionsPage activeGame={activeGame} setActiveGame={setActiveGame} dbGames={dbGames} />} />
        <Route path="/colecoes/:gameId/set/:setId" element={<CollectionCardsPage activeGame={activeGame} setActiveGame={setActiveGame} dbGames={dbGames} />} />
        <Route path="/colecoes/set/:setId" element={<CollectionCardsPage activeGame={activeGame} setActiveGame={setActiveGame} dbGames={dbGames} />} />
        <Route path="/colecao/:setId" element={<CollectionCardsPage activeGame={activeGame} setActiveGame={setActiveGame} dbGames={dbGames} />} />
        <Route path="/pastas" element={<GuestGate featureName="Pastas"><FoldersPage /></GuestGate>} />
        <Route path="/pastas/:folderId" element={<GuestGate featureName="Pastas"><FolderDetails /></GuestGate>} />
        <Route path="/produtos" element={<GuestGate featureName="Produtos"><Products onAddToCart={addToCart} activeGame={activeGame} setActiveGame={setActiveGame} /></GuestGate>} />
        <Route path="/produtos/:slug" element={<GuestGate featureName="Produtos"><Products onAddToCart={addToCart} activeGame={activeGame} setActiveGame={setActiveGame} /></GuestGate>} />
        <Route path="/ingressos" element={<GuestGate featureName="Ingressos"><Tickets onAddToCart={addToCart} activeGame={activeGame} setActiveGame={setActiveGame} /></GuestGate>} />
        <Route path="/tickets" element={<Navigate to="/ingressos" replace />} />
        <Route path="/deckbuilder" element={<GuestGate featureName="Deckbuilder"><DeckBuilderPage activeGame={activeGame} setActiveGame={setActiveGame} dbGames={dbGames} /></GuestGate>} />
        <Route path="/meus-decks" element={<GuestGate featureName="Meus Decks"><MyDecksPage activeGame={activeGame} setActiveGame={setActiveGame} dbGames={dbGames} /></GuestGate>} />
        <Route path="/gerenciar-decks" element={<Navigate to="/meus-decks" replace />} />
        <Route path="/decks" element={<DecksPage activeGame={activeGame} setActiveGame={setActiveGame} dbGames={dbGames} />} />
        <Route path="/deck/:deckId" element={<DeckView />} />
        <Route path="/ferramentas" element={<Tools activeGame={activeGame} setActiveGame={setActiveGame} dbGames={dbGames} />} />
        <Route path="/lojas" element={<Stores />} />
        <Route path="/loja/:id" element={<StoreProfile onAddToCart={addToCart} />} />
        <Route path="/evento/:id" element={<GuestGate featureName="Torneios"><EventDetails onAddToCart={addToCart} /></GuestGate>} />
        <Route path="/torneio/:id" element={<GuestGate featureName="Torneios"><EventDetails onAddToCart={addToCart} /></GuestGate>} />
        <Route path="/perfil" element={<GuestGate featureName="Perfil"><Profile /></GuestGate>} />
        <Route path="/perfil/conexoes" element={<GuestGate featureName="Conexões"><UserConnections /></GuestGate>} />
        <Route path="/perfil/seguidores" element={<Navigate to="/perfil/conexoes?tab=seguidores" replace />} />
        <Route path="/perfil/seguindo" element={<Navigate to="/perfil/conexoes?tab=seguindo" replace />} />
        <Route path="/perfil/:userId" element={<GuestGate featureName="Perfil"><Profile /></GuestGate>} />
        <Route path="/perfil/:userId/conexoes" element={<GuestGate featureName="Conexões"><UserConnections /></GuestGate>} />
        <Route path="/perfil/:userId/seguidores" element={<UserConnectionsRedirect tab="seguidores" />} />
        <Route path="/perfil/:userId/seguindo" element={<UserConnectionsRedirect tab="seguindo" />} />
        <Route path="/perfil/editar" element={<GuestGate featureName="Perfil"><EditProfile /></GuestGate>} />
        <Route path="/perfil/enderecos" element={<GuestGate featureName="Perfil"><ManageAddresses /></GuestGate>} />
        <Route path="/profile" element={<GuestGate featureName="Perfil"><ProfileRedirect /></GuestGate>} />
        <Route path="/profile/conexoes" element={<Navigate to="/perfil/conexoes" replace />} />
        <Route path="/profile/:userId" element={<GuestGate featureName="Perfil"><ProfileRedirect /></GuestGate>} />
        <Route path="/profile/:userId/conexoes" element={<GuestGate featureName="Conexões"><UserConnections /></GuestGate>} />
        <Route path="/profile/editar" element={<GuestGate featureName="Perfil"><Navigate to="/perfil/editar" replace /></GuestGate>} />
        <Route path="/profile/enderecos" element={<GuestGate featureName="Perfil"><Navigate to="/perfil/enderecos" replace /></GuestGate>} />
        <Route path="/notificacoes" element={<GuestGate featureName="Notificações"><Notifications /></GuestGate>} />
        <Route path="/cardlink" element={<GuestGate featureName="Cardlink"><Trades /></GuestGate>} />
        <Route path="/trocas" element={<Navigate to="/cardlink" replace />} />
        <Route path="/torneios" element={<GuestGate featureName="Torneios"><Tournaments activeGame={activeGame} setActiveGame={setActiveGame} /></GuestGate>} />
        <Route path="/reports" element={<GuestGate featureName="Reports"><Reports activeGame={activeGame} setActiveGame={setActiveGame} /></GuestGate>} />
        <Route path="/meta" element={<GuestGate featureName="Meta"><Meta activeGame={activeGame} setActiveGame={setActiveGame} /></GuestGate>} />
        <Route path="/comunidade" element={<GuestGate featureName="Comunidade"><Social /></GuestGate>} />
        <Route path="/pedidos" element={<GuestGate featureName="Pedidos"><Orders /></GuestGate>} />
        <Route path="/pedidos-recebidos" element={<GuestGate featureName="Pedidos Recebidos"><ReceivedOrders /></GuestGate>} />
        <Route path="/pedidos-recebidos/:id" element={<GuestGate featureName="Pedidos Recebidos"><LojistaOrderDetails /></GuestGate>} />
        <Route path="/pedido/:id" element={<GuestGate featureName="Pedidos"><OrderDetails /></GuestGate>} />
        <Route path="/meu-estoque" element={<GuestGate featureName="Estoque"><ManageStock /></GuestGate>} />
        <Route path="/gerenciar-estoque" element={<GuestGate featureName="Estoque"><ManageStock /></GuestGate>} />
        <Route path="/meu-inventario" element={<GuestGate featureName="Inventário"><ManageInventory /></GuestGate>} />
        <Route path="/gerenciar-inventario" element={<GuestGate featureName="Inventário"><ManageInventory /></GuestGate>} />
        <Route path="/inventario" element={<GuestGate featureName="Inventário"><ManageInventory /></GuestGate>} />
        <Route path="/gerenciar-creditos" element={<GuestGate featureName="Créditos"><ManageCustomerCredits /></GuestGate>} />
        <Route path="/creditos-clientes" element={<GuestGate featureName="Créditos"><ManageCustomerCredits /></GuestGate>} />
        <Route path="/meus-creditos-loja" element={<GuestGate featureName="Créditos"><ManageCustomerCredits /></GuestGate>} />
        <Route path="/minha-loja" element={<GuestGate featureName="Minha Loja"><ManageStore /></GuestGate>} />
        <Route path="/minha-loja/horarios" element={<GuestGate featureName="Horários de Atendimento"><StoreHours /></GuestGate>} />
        <Route path="/minha-loja/agenda" element={<GuestGate featureName="Agenda Semanal"><StoreSchedule /></GuestGate>} />
        <Route path="/minha-loja/jogos" element={<GuestGate featureName="Jogos Suportados"><StoreGames /></GuestGate>} />
        <Route path="/minha-loja/frete-pagamento" element={<GuestGate featureName="Frete & Pagamento"><StoreShippingPayment /></GuestGate>} />
        <Route path="/minha-loja/envio-pagamento" element={<Navigate to="/minha-loja/frete-pagamento" replace />} />
        <Route path="/minha-loja/mercadopago" element={<GuestGate featureName="Mercado Pago"><MercadoPagoSetup /></GuestGate>} />
        <Route path="/minha-loja/upload" element={<Navigate to="/minha-loja" replace />} />
        <Route path="/minha-loja/cloudinary" element={<Navigate to="/minha-loja" replace />} />
        <Route path="/upload-imagem" element={<Navigate to="/minha-loja" replace />} />
        <Route path="/meus-torneios" element={<GuestGate featureName="Meus Torneios"><ManageTournaments /></GuestGate>} />
        <Route path="/meus-torneios/importar" element={<GuestGate featureName="Importar Resultados"><ImportTournamentResults /></GuestGate>} />
        <Route path="/importar-resultados" element={<Navigate to="/meus-torneios/importar" replace />} />
        <Route path="/torneio/:id/gerenciar" element={<GuestGate featureName="Gerenciar Torneio"><ManageTournamentDetails /></GuestGate>} />
        <Route path="/novo-torneio" element={<GuestGate featureName="Novo Torneio"><CreateTournament /></GuestGate>} />
        <Route path="/pedido-balcao" element={<GuestGate featureName="Pedido no Balcão"><PedidoBalcao cart={cart} updateQuantity={updateQuantity} removeFromCart={removeFromCart} clearCart={clearCart} addToCart={addToCart} /></GuestGate>} />
        <Route path="/minha-loja/balcao" element={<Navigate to="/pedido-balcao" replace />} />
        <Route path="/carrinho" element={<GuestGate featureName="Carrinho"><CartPage cart={cart} updateQuantity={updateQuantity} removeFromCart={removeFromCart} clearCart={clearCart} addToCart={addToCart} /></GuestGate>} />
        <Route path="/glossario" element={<GlossaryPage />} />
        <Route path="/glossario/:gameSlug" element={<GlossaryPage />} />
        <Route path="/como-jogar" element={<HowToPlayPage activeGame={activeGame} setActiveGame={setActiveGame} dbGames={dbGames} />} />
        <Route path="/como-jogar/:gameSlug" element={<HowToPlayPage activeGame={activeGame} setActiveGame={setActiveGame} dbGames={dbGames} />} />
        <Route path="/regras" element={<Navigate to="/como-jogar" replace />} />
        <Route path="/regras/:gameSlug" element={<HowToPlayPage activeGame={activeGame} setActiveGame={setActiveGame} dbGames={dbGames} />} />
        <Route path="/configuracoes" element={<SettingsPage />} />
        <Route path="/settings" element={<Navigate to="/configuracoes" replace />} />
        <Route path="/suporte" element={<SupportPage />} />
        <Route path="/termos" element={<TermsPage />} />
        <Route path="/termos-e-condicoes" element={<TermsPage />} />
        <Route path="/termos-do-lojista" element={<LojistaTermsPage />} />
        <Route path="/minha-loja/termos" element={<Navigate to="/termos-do-lojista" replace />} />
        <Route path="/terms" element={<Navigate to="/termos" replace />} />
        <Route path="/politica-de-privacidade" element={<Navigate to="/termos?tab=privacidade" replace />} />
        <Route path="/admin/stats" element={<Navigate to="/reports" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
    </>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <NotificationProvider>
          <SettingsProvider>
            <LoginPromptProvider>
              <AppContent />
            </LoginPromptProvider>
          </SettingsProvider>
        </NotificationProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
